/**
 * App lock state management.
 *
 * Locks the app when it goes to background (visibility change) after a
 * configurable grace period. Unlock via password (primary) or biometric (optional).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SecurityPolicy } from './security-policy';

const LOCK_STATE_KEY = 'zbk3_lock_state';

interface LockState {
  /** Whether the app is currently locked */
  isLocked: boolean;
  /** Lock the app immediately */
  lock: () => void;
  /** Unlock the app */
  unlock: () => void;
}

function persistLockState(locked: boolean): void {
  try {
    sessionStorage.setItem(LOCK_STATE_KEY, locked ? '1' : '0');
  } catch {
    // Ignore
  }
}

function getPersistedLockState(): boolean {
  try {
    return sessionStorage.getItem(LOCK_STATE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Hook that manages lock state based on document visibility changes.
 *
 * When the app goes to background:
 * - Starts a grace period timer (configurable via policy)
 * - If the app returns to foreground within the grace period, no lock
 * - If the grace period expires while in background, locks the app
 *
 * Lock state is persisted in sessionStorage (survives page refresh, not tab close).
 */
export function useLockState(
  policy: SecurityPolicy,
  /** Only activate lock when user is authenticated */
  isAuthenticated: boolean,
): LockState {
  const [isLocked, setIsLocked] = useState(getPersistedLockState);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lock = useCallback(() => {
    setIsLocked(true);
    persistLockState(true);
  }, []);

  const unlock = useCallback(() => {
    setIsLocked(false);
    persistLockState(false);
  }, []);

  useEffect(() => {
    // Lock screen disabled for now — will be enabled in a future sprint
    return;

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        // App went to background — start grace period
        graceTimerRef.current = setTimeout(() => {
          lock();
        }, policy.lockGracePeriodSeconds * 1000);
      } else {
        // App returned to foreground — cancel grace period if still running
        if (graceTimerRef.current) {
          clearTimeout(graceTimerRef.current);
          graceTimerRef.current = null;
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
      }
    };
  }, [isAuthenticated, policy.requireLockOnBackground, policy.lockGracePeriodSeconds, lock]);

  return { isLocked, lock, unlock };
}
